import { useState, useEffect } from 'react';

export interface CurrentWeather {
  temperature: number;
  apparentTemperature: number;
  weatherCode: number;
  windSpeed: number;
  windDirection: number;
}

export interface HourlyWeather {
  time: string;
  temperature: number;
  apparentTemperature: number;
  weatherCode: number;
  precipitationProbability: number;
  windSpeed: number;
  windDirection: number;
}

export interface WeatherResult {
  current: CurrentWeather;
  hourly: HourlyWeather[];
}

export interface UseWeatherResult {
  weather: WeatherResult | null;
  loading: boolean;
}

interface WeatherCache {
  data: WeatherResult;
  timestamp: number;
  cachedLat: number;
  cachedLng: number;
}

let cache: WeatherCache | null = null;

const CACHE_TTL_MS = 30 * 60 * 1000;
const CACHE_RADIUS_KM = 1;

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function clearWeatherCache() {
  cache = null;
}

function isCacheValid(lat: number, lng: number): boolean {
  if (!cache) return false;
  const age = Date.now() - cache.timestamp;
  if (age > CACHE_TTL_MS) return false;
  return haversineKm(lat, lng, cache.cachedLat, cache.cachedLng) <= CACHE_RADIUS_KM;
}

async function fetchWeather(lat: number, lng: number): Promise<WeatherResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  try {
    const url =
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${lat}&longitude=${lng}` +
      `&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,wind_direction_10m` +
      `&hourly=temperature_2m,apparent_temperature,precipitation_probability,weather_code,wind_speed_10m,wind_direction_10m` +
      `&forecast_hours=24` +
      `&wind_speed_unit=kmh` +
      `&timezone=Asia%2FTokyo`;

    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = (await res.json()) as {
      current: {
        temperature_2m: number;
        apparent_temperature: number;
        weather_code: number;
        wind_speed_10m: number;
        wind_direction_10m: number;
      };
      hourly: {
        time: string[];
        temperature_2m: number[];
        apparent_temperature: number[];
        precipitation_probability: number[];
        weather_code: number[];
        wind_speed_10m: number[];
        wind_direction_10m: number[];
      };
    };

    const current: CurrentWeather = {
      temperature: json.current.temperature_2m,
      apparentTemperature: json.current.apparent_temperature,
      weatherCode: json.current.weather_code,
      windSpeed: json.current.wind_speed_10m,
      windDirection: json.current.wind_direction_10m,
    };

    // Open-MeteoはAsia/Tokyo（UTC+9）で時刻を返すため、現在時刻もJSTに変換して比較
    const nowJST = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 16);
    const startIdx = json.hourly.time.findIndex((t) => t > nowJST);
    const from = startIdx === -1 ? 0 : startIdx;

    const hourly: HourlyWeather[] = json.hourly.time.slice(from, from + 8).map((time, i) => ({
      time,
      temperature: json.hourly.temperature_2m[from + i],
      apparentTemperature: json.hourly.apparent_temperature[from + i],
      weatherCode: json.hourly.weather_code[from + i],
      precipitationProbability: json.hourly.precipitation_probability[from + i] ?? 0,
      windSpeed: json.hourly.wind_speed_10m[from + i],
      windDirection: json.hourly.wind_direction_10m[from + i],
    }));

    return { current, hourly };
  } finally {
    clearTimeout(timeoutId);
  }
}

export function getWeatherInfo(code: number): { emoji: string; label: string } {
  if (code === 0) return { emoji: '☀️', label: '快晴' };
  if (code === 1) return { emoji: '🌤', label: '晴れ' };
  if (code === 2) return { emoji: '⛅', label: '晴れ時々曇り' };
  if (code === 3) return { emoji: '☁️', label: '曇り' };
  if (code === 45 || code === 48) return { emoji: '🌫', label: '霧' };
  if (code >= 51 && code <= 55) return { emoji: '🌦', label: '霧雨' };
  if (code >= 61 && code <= 65) return { emoji: '🌧', label: '雨' };
  if (code >= 71 && code <= 77) return { emoji: '❄️', label: '雪' };
  if (code >= 80 && code <= 82) return { emoji: '🌦', label: 'にわか雨' };
  if (code >= 95 && code <= 99) return { emoji: '⛈', label: '雷雨' };
  return { emoji: '🌡', label: '不明' };
}

export function getWindDirectionText(degree: number): string {
  const d = ((degree % 360) + 360) % 360;
  if (d < 22.5 || d >= 337.5) return '北';
  if (d < 67.5) return '北東';
  if (d < 112.5) return '東';
  if (d < 157.5) return '南東';
  if (d < 202.5) return '南';
  if (d < 247.5) return '南西';
  if (d < 292.5) return '西';
  return '北西';
}

export function getWindArrow(degree: number): string {
  const d = ((degree % 360) + 360) % 360;
  // 風が吹いてくる方向の逆向きに矢印を向ける（北風 → ↓）
  if (d < 22.5 || d >= 337.5) return '↓';
  if (d < 67.5) return '↙';
  if (d < 112.5) return '←';
  if (d < 157.5) return '↖';
  if (d < 202.5) return '↑';
  if (d < 247.5) return '↗';
  if (d < 292.5) return '→';
  return '↘';
}

export function useWeather(lat: number | null, lng: number | null): UseWeatherResult {
  const [weather, setWeather] = useState<WeatherResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lat === null || lng === null) return;

    if (isCacheValid(lat, lng)) {
      setWeather(cache!.data);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchWeather(lat, lng)
      .then((data) => {
        if (cancelled) return;
        cache = { data, timestamp: Date.now(), cachedLat: lat, cachedLng: lng };
        setWeather(data);
      })
      .catch(() => {
        if (cancelled) return;
        setWeather(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  return { weather, loading };
}
